/**
 * Safely handles ActiveCampaign proc.php responses without eval or JSONP.
 */
import { parse } from "acorn";
import { Sentry } from "./sentryInstrument";
import "./i18nFallback";

const PROC_PHP_PATTERN = /activehosted\.com\/proc\.php/;
const PROC_ENDPOINT =
  "https://reviveourhearts.activehosted.com/proc.php?jsonp=true";

const FAILURE_MESSAGE = window.gettext(
  "Sorry, your submission failed. Please try again."
);
const LONG_PAYLOAD_FAILURE_MESSAGE = window.gettext(
  "Sorry, your submission failed. Please shorten your responses and try again."
);

// The only ActiveCampaign callbacks we replay.
const NOTIFICATION_DEFAULT_MESSAGES = new Map([
  ["_show_thank_you", window.gettext("Thank you for subscribing!")],
  ["_show_error", FAILURE_MESSAGE],
  ["_show_unsubscribe", ""],
  ["_show_pc_confirmation", ""],
]);

const UNSAFE = Symbol("unsafe");
const SENTRY_JS_MAX_LENGTH = 500;

function truncateForSentry(value, maxLength = SENTRY_JS_MAX_LENGTH) {
  if (typeof value !== "string") {
    return value;
  }
  return value.length <= maxLength
    ? value
    : `${value.slice(0, maxLength)}...`;
}

function sentryContext(formId, js) {
  return {
    extra: {
      formId,
      js: truncateForSentry(js),
    },
  };
}

// Accept only static values with no runtime behavior.
function staticValue(node) {
  switch (node.type) {
    case "Literal":
      return node.regex ? UNSAFE : node.value;
    case "Identifier":
      return node.name === "undefined" ? undefined : UNSAFE;
    case "TemplateLiteral":
      if (node.expressions.length > 0) {
        return UNSAFE;
      }
      return node.quasis.map((quasi) => quasi.value.cooked).join("");
    case "UnaryExpression": {
      if (!node.prefix || (node.operator !== "-" && node.operator !== "+")) {
        return UNSAFE;
      }
      const value = staticValue(node.argument);
      if (typeof value !== "number") {
        return UNSAFE;
      }
      return node.operator === "-" ? -value : value;
    }
    default:
      return UNSAFE;
  }
}

/** @param {unknown} value @returns {string | null} */
function safeHttpUrl(value) {
  if (typeof value !== "string" || !value) {
    return null;
  }
  try {
    const url = new URL(value);
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return null;
    }
    return url.href;
  } catch {
    return null;
  }
}

function performRedirect(url) {
  try {
    window.top.location.href = url;
  } catch {
    window.location.href = url;
  }
}

// Allows `window.top.location`, `window.location`, `top.location`, and `location`.
function isLocationTarget(node) {
  if (node.type === "Identifier") {
    return node.name === "location";
  }
  if (
    node.type !== "MemberExpression" ||
    node.computed ||
    node.property.type !== "Identifier" ||
    node.property.name !== "location"
  ) {
    return false;
  }
  const object = node.object;
  if (
    object.type === "Identifier" &&
    (object.name === "window" || object.name === "top")
  ) {
    return true;
  }
  return (
    object.type === "MemberExpression" &&
    !object.computed &&
    object.object.type === "Identifier" &&
    object.object.name === "window" &&
    object.property.type === "Identifier" &&
    object.property.name === "top"
  );
}

function isRedirectAssignmentLeft(node) {
  if (isLocationTarget(node)) {
    return true;
  }
  return (
    node.type === "MemberExpression" &&
    !node.computed &&
    node.property.type === "Identifier" &&
    node.property.name === "href" &&
    isLocationTarget(node.object)
  );
}

/** @param {unknown} data @returns {string | null} */
function redirectUrlFromData(data) {
  if (data?.action !== "redirect") {
    return null;
  }
  return safeHttpUrl(data?.data?.url ?? data?.url);
}

/** @param {unknown} expression @returns {string | null} */
function redirectUrlFromAssignment(expression) {
  if (
    expression.type !== "AssignmentExpression" ||
    expression.operator !== "="
  ) {
    return null;
  }
  if (!isRedirectAssignmentLeft(expression.left)) {
    return null;
  }
  const value = staticValue(expression.right);
  return typeof value === "string" ? safeHttpUrl(value) : null;
}

// Allows `_show_*()` and `window._show_*()` forms.
function notificationName(callee) {
  let name = null;
  if (callee.type === "Identifier") {
    name = callee.name;
  } else if (
    callee.type === "MemberExpression" &&
    !callee.computed &&
    callee.object.type === "Identifier" &&
    callee.object.name === "window" &&
    callee.property.type === "Identifier"
  ) {
    name = callee.property.name;
  }
  return NOTIFICATION_DEFAULT_MESSAGES.has(name) ? name : null;
}

/**
 * @param {string} js
 * @returns {{calls: {name: string, args: unknown[]}[], redirectUrl: string | null, parseError: Error | null}}
 */
function analyzeProcResponse(js) {
  let program;
  let parseError = null;
  try {
    program = parse(js, { ecmaVersion: "latest" });
  } catch (error) {
    parseError = error instanceof Error ? error : new Error(String(error));
    return { calls: [], redirectUrl: null, parseError };
  }

  const calls = [];
  let redirectUrl = null;
  for (const statement of program.body) {
    if (statement.type !== "ExpressionStatement") {
      continue;
    }
    const expression = statement.expression;
    if (expression.type === "AssignmentExpression") {
      redirectUrl ||= redirectUrlFromAssignment(expression);
      continue;
    }
    if (expression.type !== "CallExpression") {
      continue;
    }
    const name = notificationName(expression.callee);
    if (!name) {
      continue;
    }
    const args = expression.arguments.map((argument) =>
      argument.type === "SpreadElement" ? UNSAFE : staticValue(argument)
    );
    if (!args.includes(UNSAFE)) {
      calls.push({ name, args });
    }
  }
  return { calls, redirectUrl, parseError: null };
}

/** @param {string} js @returns {{name: string, args: unknown[]}[]} */
function extractNotificationCalls(js) {
  return analyzeProcResponse(js).calls;
}

function resetSubmitButton(formId) {
  if (!formId) {
    return;
  }
  const button = document.querySelector(`#_form_${formId}_submit`);
  if (button) {
    button.disabled = false;
    button.classList.remove("processing");
  }
}

function showError(formId, message) {
  if (typeof window._show_error === "function" && formId) {
    window._show_error(formId, message);
  }
}

// Replays a safe callback with local defaults.
function dispatchNotification(name, args, formId) {
  const fn = window[name];
  if (typeof fn !== "function") {
    return;
  }
  const finalArgs = [...args];
  finalArgs[0] ||= formId;
  finalArgs[1] ||= NOTIFICATION_DEFAULT_MESSAGES.get(name);
  fn(...finalArgs);
}

/**
 * @param {unknown} data
 * @param {string} fallbackFormId
 * @param {boolean} [isSubmit]
 */
function handleResponse(data, fallbackFormId, isSubmit) {
  const js = typeof data?.js === "string" ? data.js.trim() : "";
  const formId = fallbackFormId || "";
  const structuredRedirectUrl = redirectUrlFromData(data);

  if (structuredRedirectUrl) {
    performRedirect(structuredRedirectUrl);
    return;
  }

  if (!js) {
    dispatchNotification("_show_thank_you", [], formId);
    return;
  }

  const { calls, redirectUrl, parseError } = analyzeProcResponse(js);
  if (parseError) {
    Sentry.captureException(parseError, {
      ...sentryContext(formId, js),
      tags: { stage: "activecampaign proc.php parse" },
    });
    showError(formId, FAILURE_MESSAGE);
    if (isSubmit) {
      resetSubmitButton(formId);
    }
    return;
  }

  if (redirectUrl) {
    performRedirect(redirectUrl);
    return;
  }

  if (calls.length > 0) {
    for (const { name, args } of calls) {
      dispatchNotification(name, args, formId);
    }
    return;
  }

  Sentry.captureMessage(
    "ActiveCampaign proc.php response contained no safe notification calls",
    {
      level: "warning",
      ...sentryContext(formId, js),
    }
  );
  showError(formId, FAILURE_MESSAGE);
  if (isSubmit) {
    resetSubmitButton(formId);
  }
}

/**
 * @param {string} url
 * @param {(() => void) | null | undefined} callback
 * @param {boolean | undefined} isSubmit
 */
async function safeProcPhpSubmit(url, callback, isSubmit) {
  const params = new URLSearchParams(url.slice(url.indexOf("?") + 1));
  const formId = params.get("f") || params.get("u") || "";
  const formData = new FormData();
  params.forEach((value, key) => {
    if (key !== "hideButton") {
      formData.append(key, value);
    }
  });

  const headers = { Accept: "application/json" };
  const pageUrlParams = new URLSearchParams(window.location.search);
  if (pageUrlParams.has("t")) {
    headers.Authorization = `Bearer ${pageUrlParams.get("t")}`;
  }

  try {
    const response = await fetch(PROC_ENDPOINT, {
      method: "POST",
      headers,
      body: formData,
    });
    const data = await response.json();
    handleResponse(data, formId, isSubmit);
    callback?.();
  } catch (error) {
    Sentry.captureException(error, {
      extra: { formId },
      tags: { stage: "activecampaign proc.php fetch" },
    });
    showError(
      formId,
      url.length > 10000 ? LONG_PAYLOAD_FAILURE_MESSAGE : FAILURE_MESSAGE
    );
    resetSubmitButton(formId);
  }
}

function installOverride() {
  const original = window._load_script;
  window._load_script = function (url, callback, isSubmit) {
    if (typeof url === "string" && PROC_PHP_PATTERN.test(url)) {
      return safeProcPhpSubmit(url, callback, isSubmit);
    }
    if (typeof original === "function") {
      return original.apply(this, arguments);
    }
  };
}

document.addEventListener("DOMContentLoaded", () => {
  window.activeCampaignSafeSubmit = {
	handleResponse,
	extractNotificationCalls,
	install: installOverride,
  };

  installOverride();
});

